import React, { useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { useDropzone } from 'react-dropzone'
import { CellHyperlinkValue, CellRichTextValue, Workbook } from 'exceljs'
import Papa from 'papaparse'
import { Address, ContactSchema } from '../core/_models'
import z from 'zod'
import {
    importContact,
    previewImportContact,
    ImportPreview,
    ImportPreviewRow,
    ImportResult,
} from '../core/_requests'
import { useContactImport } from '../core/ContactImportProvider'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { KTSVG, toAbsoluteUrl } from '../../../../../../_metronic/helpers'

const ContactImportSchama = ContactSchema.extend({
    name: z.string().optional(),
    workEmail: z.string().optional(),
    phone: z.string().optional(),
    fax: z.string().optional(),
    website: z.string().optional(),
    facebook: z.string().optional(),
    linkedIn: z.string().optional(),
    instagram: z.string().optional(),
    email: z.string().optional(),
    mobileNumber: z.string().optional(),
    workNumber: z.string().optional(),
    maplink: z.string().optional(),
})

function isRichValue(value: any) {
    return Boolean(value && Array.isArray(value.richText))
}

type ContactImport = z.infer<typeof ContactImportSchama>

type tableDataType = {
    header: string[]
    data: ContactImport[]
    // Shown to the user so a mistyped header or a dropped row isn't silent.
    fileName: string
    ignoredHeaders: string[]
}

const emptyTable: tableDataType = {
    header: [],
    data: [],
    fileName: '',
    ignoredHeaders: [],
}

// Columns are stored as groups, so a row only counts as "has something in it"
// when one of the plain columns was filled.
const GROUP_KEYS = ['emails', 'phones', 'socialUrls', 'address']
const rowHasContent = (rowData: any) =>
    Object.entries(rowData).some(
        ([key, value]) =>
            !GROUP_KEYS.includes(key) &&
            value !== undefined &&
            value !== null &&
            `${value}`.trim() !== ''
    )

// The whole sheet is posted as one JSON request, and the server accepts at most
// 100kb of body (Express's default — nothing in main.ts raises it). Measured at
// roughly 690 bytes per contact, so about 150 contacts fit. Warn a little below
// the ceiling, because a sheet with long notes or addresses runs heavier.
const MAX_PAYLOAD_BYTES = 102400
const WARN_PAYLOAD_BYTES = 92160
const TYPICAL_BYTES_PER_CONTACT = 690
const APPROX_MAX_CONTACTS = Math.floor(MAX_PAYLOAD_BYTES / TYPICAL_BYTES_PER_CONTACT)

// The preview table shows every row that could plausibly be imported, so
// customers never see "only 50" and wonder if the rest were dropped. Tied to
// the same ceiling as the payload check above, so it can't fall behind if
// that number ever changes.
const PREVIEW_ROW_LIMIT = APPROX_MAX_CONTACTS

// The server reports changes by stored field name, but the table shows sheet
// columns — and several columns feed one stored field.
const FIELD_TO_COLUMNS: Record<string, string[]> = {
    firstName: ['firstName', 'name'],
    emails: ['email', 'workEmail'],
    phones: ['mobileNumber', 'workNumber', 'fax', 'phone'],
    socialUrls: ['website', 'facebook', 'linkedIn', 'instagram'],
    address: ['address', 'maplink'],
}

const changedColumnsFor = (row?: ImportPreviewRow): Set<string> => {
    const columns = new Set<string>()
    row?.changedFields?.forEach((field) => {
        ;(FIELD_TO_COLUMNS[field] || [field]).forEach((column) => columns.add(column))
    })
    return columns
}

function DropZone() {
    let [tableData, setTableData] = useState<tableDataType>(emptyTable)
    let [fileError, setFileError] = useState('')
    let [preview, setPreview] = useState<ImportPreview | undefined>(undefined)
    let [isPreviewing, setIsPreviewing] = useState(false)
    let [importResult, setImportResult] = useState<ImportResult | undefined>(undefined)
    let [isSubmitting, setIsSubmitting] = useState(false)
    const { setState } = useContactImport()
    const { refetch } = useQueryResponse()

    function readExcel(file: File) {
        const fileName = file.name
        const reader = new FileReader()

        reader.onabort = () => console.log('file reading was aborted')
        reader.onerror = () => console.log('file reading has failed')
        reader.onload = async () => {
            // Do whatever you want with the file contents
            const binaryStr = reader.result as ArrayBuffer
            let workbook = new Workbook()

            await workbook.xlsx.load(binaryStr)

            let worksheet = workbook.getWorksheet(1)
            const headerMap = new Map<number, keyof ContactImport>()

            const headers = worksheet.getRow(1).values as string[]
            const ignoredHeaders: string[] = []

            headers.forEach((value: string, index: number) => {
                let key = value as string
                if (key === 'mobile number') {
                    key = 'mobileNumber'
                }
                if (key === 'MobileNumber') {
                    key = 'mobileNumber'
                }
                if (key === 'work number') {
                    key = 'workNumber'
                }
                if (key === 'Website') {
                    key = 'website'
                }
                if (key === 'Facebook') {
                    key = 'facebook'
                }
                if (key === 'Instagram') {
                    key = 'instagram'
                }
                if (key === 'LinkedIn') {
                    key = 'linkedIn'
                }

                try {
                    let validatedKey = ContactImportSchama.keyof().parse(key)
                    headerMap.set(index, validatedKey)
                } catch (e) {
                    // Header names are case sensitive, so "Email" lands here and
                    // that whole column would otherwise vanish without a word.
                    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
                        ignoredHeaders.push(`${value}`)
                    }
                }
            })
            let excelData: ContactImport[] = []
            worksheet.eachRow({ includeEmpty: true }, function (row, rowNumber) {
                if (rowNumber === 1) {
                    return
                }

                if (row == null || row === undefined) {
                    return
                }

                if (!row.values.entries == null || row.values.entries === undefined) {
                    return
                }

                let rowData: any = {}
                rowData['emails'] = []
                rowData['phones'] = []
                rowData['socialUrls'] = []
                rowData['address'] = [{ label: 'Home' }, { label: 'Work' }]
                for (const [index, element] of (
                    row.values as string[] | CellHyperlinkValue[]
                ).entries()) {
                    let key = headerMap.get(index)
                    if (key) {
                        if (element instanceof Object && Object.hasOwn(element, 'text')) {
                            rowData[key] = element.text
                        } else if (
                            element instanceof Object &&
                            Object.hasOwn(element, 'richText')
                        ) {
                            rowData[key] = (element as unknown as CellRichTextValue).richText
                                .map(({ text }) => text)
                                .join('')
                        } else {
                            rowData[key] = element
                        }

                        if (key === 'address') {
                            rowData[key] = [
                                { label: 'Home' },
                                { label: 'Work', street: rowData[key] },
                            ]
                        }

                        if (key === 'maplink') {
                            // If you want to add maplink to the address, do it here
                            if (rowData['address'] && Array.isArray(rowData['address'])) {
                                rowData['address'][1].link = rowData[key]
                            }
                        }

                        if (key === 'name') {
                            rowData['firstName'] = rowData[key]
                        }

                        // if (key === 'note') {
                        //     rowData['note'] = rowData[key]
                        // }

                        if (key === 'workEmail') {
                            rowData['emails'] = [
                                ...rowData['emails'],
                                { label: 'Work', value: rowData[key] },
                            ]
                        }
                        if (key === 'email') {
                            rowData['emails'] = [
                                ...rowData['emails'],
                                { label: 'Work', value: rowData[key] },
                            ]
                        }

                        if (key === 'mobileNumber') {
                            rowData['phones'] = [
                                ...rowData['phones'],
                                { label: 'Mobile', value: rowData[key] },
                            ]
                        }
                        if (key === 'workNumber') {
                            rowData['phones'] = [
                                ...rowData['phones'],
                                { label: 'Work', value: rowData[key] },
                            ]
                        }
                        if (key === 'fax') {
                            rowData['phones'] = [
                                ...rowData['phones'],
                                { label: 'fax', value: rowData[key] },
                            ]
                        }
                        if (key === 'website') {
                            rowData['socialUrls'] = [
                                ...rowData['socialUrls'],
                                { label: 'Website', value: rowData[key] },
                            ]
                        }
                        if (key === 'facebook') {
                            rowData['socialUrls'] = [
                                ...rowData['socialUrls'],
                                { label: 'Facebook', value: rowData[key] },
                            ]
                        }
                        if (key === 'linkedIn') {
                            rowData['socialUrls'] = [
                                ...rowData['socialUrls'],
                                { label: 'LinkedIn', value: rowData[key] },
                            ]
                        }
                        if (key === 'instagram') {
                            rowData['socialUrls'] = [
                                ...rowData['socialUrls'],
                                { label: 'Instagram', value: rowData[key] },
                            ]
                        }
                    }
                }
                let validatedData = ContactImportSchama.safeParse(rowData)

                // Every row with something in it is sent. Whether it can import is
                // the server's call: an update needs no first name (blank keeps the
                // stored one) — only a brand-new contact does.
                if (rowHasContent(rowData)) {
                    excelData.push(
                        validatedData.success ? validatedData.data : (rowData as ContactImport)
                    )
                }
            })

            setTableData({
                header: [...headerMap.values()],
                data: excelData,
                fileName,
                ignoredHeaders,
            })
        }
        reader.readAsArrayBuffer(file)
    }

    function readCSV(file: File) {
        const fileName = file.name
        const reader = new FileReader()

        reader.onabort = () => console.log('file reading was aborted')
        reader.onerror = () => console.log('file reading has failed')
        reader.onload = async () => {
            // Do whatever you want with the file contents
            const text = reader.result as string

            let result = Papa.parse(text, { skipEmptyLines: true }) as Papa.ParseResult<string[]>

            let headers = result.data[0]
            const headerMap = new Map<number, keyof ContactImport>()
            const ignoredHeaders: string[] = []

            headers.forEach((value: string, index: number) => {
                let key = value as string
                try {
                    let validatedKey = ContactImportSchama.keyof().parse(key)
                    headerMap.set(index, validatedKey)
                } catch (e) {
                    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
                        ignoredHeaders.push(`${value}`)
                    }
                }
            })

            let dataList: ContactImport[] = []

            result.data.slice(1).forEach((row) => {
                let rowData: any = {}
                rowData['emails'] = []
                rowData['phones'] = []
                rowData['socialUrls'] = []

                for (const [index, element] of row.entries()) {
                    let key = headerMap.get(index)
                    if (key) {
                        if (key === 'address') {
                            continue
                        }
                        rowData[key] = element

                        if (key === 'workEmail') {
                            rowData['emails'] = [{ label: 'Work', value: rowData[key] }]
                        }
                        if (key === 'phone') {
                            rowData['phones'] = [{ label: 'Mobile', value: rowData[key] }]
                        }
                        if (key === 'fax') {
                            rowData['phones'] = [
                                ...rowData['phones'],
                                { label: 'fax', value: rowData[key] },
                            ]
                        }
                        if (key === 'website') {
                            rowData['socialUrls'] = [
                                ...rowData['socialUrls'],
                                { label: 'Website', value: rowData[key] },
                            ]
                        }
                        if (key === 'facebook') {
                            rowData['socialUrls'] = [
                                ...rowData['socialUrls'],
                                { label: 'Facebook', value: rowData[key] },
                            ]
                        }
                        if (key === 'linkedIn') {
                            rowData['socialUrls'] = [
                                ...rowData['socialUrls'],
                                { label: 'LinkedIn', value: rowData[key] },
                            ]
                        }
                        if (key === 'instagram') {
                            rowData['socialUrls'] = [
                                ...rowData['socialUrls'],
                                { label: 'Instagram', value: rowData[key] },
                            ]
                        }
                    }
                }
                let validatedData = ContactImportSchama.safeParse(rowData)

                // Every row with something in it is sent. Whether it can import is
                // the server's call: an update needs no first name (blank keeps the
                // stored one) — only a brand-new contact does.
                if (rowHasContent(rowData)) {
                    dataList.push(
                        validatedData.success ? validatedData.data : (rowData as ContactImport)
                    )
                }
            })

            setTableData({
                header: [...headerMap.values()],
                data: dataList,
                fileName,
                ignoredHeaders,
            })
        }
        reader.readAsText(file)
    }

    const onDrop = useCallback((acceptedFiles: File[]) => {
        setFileError('')
        setImportResult(undefined)
        acceptedFiles.forEach((file) => {
            let matchExtension = file.name.match(/\.[0-9a-z]+$/i)
            let extension = matchExtension ? matchExtension[0].toLowerCase() : ''
            if (extension === '.csv') {
                readCSV(file)
            } else if (extension === '.xlsx') {
                readExcel(file)
            } else {
                setTableData(emptyTable)
                setFileError(
                    `"${file.name}" is not a supported file. Please upload an .xlsx or .csv file.`
                )
            }
        })
    }, [])
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        maxFiles: 1,
        accept: {
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'text/csv': ['.csv'],
        },
    })

    // Whenever a file is parsed, ask the server which rows would be created and
    // which would update an existing contact. Nothing is written by this call.
    useEffect(() => {
        if (!tableData.data.length) {
            setPreview(undefined)
            return
        }
        let cancelled = false
        setIsPreviewing(true)
        previewImportContact(tableData.data)
            .then((result) => {
                if (!cancelled) setPreview(result)
            })
            .catch(() => {
                // A failed dry run must not block the import itself.
                if (!cancelled) setPreview(undefined)
            })
            .finally(() => {
                if (!cancelled) setIsPreviewing(false)
            })
        return () => {
            cancelled = true
        }
    }, [tableData])

    // What will actually go over the wire, so the warning reflects this sheet
    // rather than a guess from the row count.
    const payloadBytes = useMemo(
        () =>
            tableData.data.length
                ? new TextEncoder().encode(JSON.stringify(tableData.data)).length
                : 0,
        [tableData]
    )
    const tooLarge = payloadBytes > WARN_PAYLOAD_BYTES
    // Once the dry run is back, count only rows that will actually import.
    const importableCount = preview ? preview.created + preview.updated : tableData.data.length

    const hasFile = tableData.header.length > 0
    const previewRows = tableData.data.slice(0, PREVIEW_ROW_LIMIT)

    const reset = () => {
        setTableData(emptyTable)
        setImportResult(undefined)
        setFileError('')
        setPreview(undefined)
    }

    return (
        <div>
            {/* begin::Intro + template */}
            <div className='d-flex flex-stack flex-wrap gap-3 mb-6'>
                <div className='text-gray-600 fs-7 me-4'>
                    Upload an <span className='fw-bold'>.xlsx</span> or{' '}
                    <span className='fw-bold'>.csv</span> file. A contact whose email address
                    already exists is updated instead of being duplicated.
                </div>
                <a
                    href={toAbsoluteUrl('/media/sample/contact-import-sample.xlsx')}
                    download
                    className='btn btn-sm btn-light-primary d-flex flex-shrink-0'
                >
                    <KTSVG
                        path='/media/icons/duotune/files/fil017.svg'
                        className='svg-icon-3 me-1'
                    />
                    Download sample template
                </a>
            </div>
            {/* end::Intro + template */}

            {/* begin::Dropzone */}
            <div
                {...getRootProps()}
                className={clsx(
                    'd-flex flex-column align-items-center justify-content-center text-center',
                    'border border-2 border-dashed rounded cursor-pointer',
                    hasFile ? 'py-5' : 'py-15',
                    isDragActive ? 'border-primary bg-light-primary' : 'border-gray-300'
                )}
            >
                <input {...getInputProps()} />
                <KTSVG
                    path='/media/icons/duotune/files/fil010.svg'
                    className={clsx(
                        hasFile ? 'svg-icon-2x' : 'svg-icon-3x',
                        isDragActive ? 'svg-icon-primary' : 'svg-icon-gray-400',
                        'mb-3'
                    )}
                />
                <div className='fs-5 fw-bold text-gray-800'>
                    {isDragActive
                        ? 'Drop the file to load it'
                        : hasFile
                        ? 'Drop another file to replace this one'
                        : 'Drag and drop your file here, or click to browse'}
                </div>
                {!hasFile && (
                    <div className='fs-7 text-gray-500 mt-1'>
                        .xlsx or .csv &middot; only the first sheet is read &middot; row 1 must be
                        the header row &middot; up to about {APPROX_MAX_CONTACTS} contacts per
                        import
                    </div>
                )}
            </div>
            {/* end::Dropzone */}

            {fileError && (
                <div className='alert alert-danger d-flex align-items-center mt-5 mb-0'>
                    <KTSVG
                        path='/media/icons/duotune/general/gen044.svg'
                        className='svg-icon-2 svg-icon-danger me-3'
                    />
                    <span>{fileError}</span>
                </div>
            )}

            {hasFile && (
                <>
                    {/* begin::File summary */}
                    <div className='d-flex flex-stack flex-wrap gap-3 mt-6 mb-4'>
                        <div className='d-flex align-items-center'>
                            <KTSVG
                                path='/media/icons/duotune/files/fil003.svg'
                                className='svg-icon-2x svg-icon-primary me-3'
                            />
                            <div>
                                <div className='fw-bold text-gray-800'>{tableData.fileName}</div>
                                <div className='fs-7 text-gray-500'>
                                    {tableData.data.length} contact
                                    {tableData.data.length === 1 ? '' : 's'} ready &middot;{' '}
                                    {tableData.header.length} column
                                    {tableData.header.length === 1 ? '' : 's'} recognised
                                </div>
                            </div>
                        </div>
                        <div className='d-flex flex-wrap gap-2'>
                            {tableData.header.map((column) => (
                                <span key={column} className='badge badge-light-primary'>
                                    {column}
                                </span>
                            ))}
                        </div>
                    </div>
                    {/* end::File summary */}

                    {/* begin::Warnings */}
                    {tableData.ignoredHeaders.length > 0 && (
                        <div className='alert alert-warning d-flex align-items-start mb-3'>
                            <KTSVG
                                path='/media/icons/duotune/general/gen044.svg'
                                className='svg-icon-2 svg-icon-warning me-3 mt-1'
                            />
                            <div>
                                <div className='fw-bold'>
                                    {tableData.ignoredHeaders.length} column
                                    {tableData.ignoredHeaders.length === 1 ? '' : 's'} will be
                                    ignored
                                </div>
                                <div className='fs-7'>
                                    {tableData.ignoredHeaders.join(', ')} &mdash; column names are
                                    case sensitive, so <code>email</code> is read but{' '}
                                    <code>Email</code> is not. Check the sample template for the
                                    exact spelling.
                                </div>
                            </div>
                        </div>
                    )}
                    {tooLarge && (
                        <div className='alert alert-warning d-flex align-items-start mb-3'>
                            <KTSVG
                                path='/media/icons/duotune/general/gen044.svg'
                                className='svg-icon-2 svg-icon-warning me-3 mt-1'
                            />
                            <div>
                                <div className='fw-bold'>
                                    This file is close to or over the size the server accepts
                                </div>
                                <div className='fs-7'>
                                    {tableData.data.length} contacts come to{' '}
                                    {Math.round(payloadBytes / 1024)}kb, and the limit is{' '}
                                    {Math.round(MAX_PAYLOAD_BYTES / 1024)}kb per import. Split the
                                    sheet into files of roughly {APPROX_MAX_CONTACTS} contacts and
                                    import them one after another — contacts already imported are
                                    matched by email, so re-importing a row updates it instead of
                                    duplicating it.
                                </div>
                            </div>
                        </div>
                    )}
                    {tableData.data.length === 0 && (
                        <div className='alert alert-danger mb-3'>
                            No usable rows were found in this file. Check that row 1 holds the
                            column names and that the rows below it have data in them.
                        </div>
                    )}
                    {/* end::Warnings */}

                    {/* begin::What this import will do */}
                    {isPreviewing && (
                        <div className='d-flex align-items-center text-gray-600 fs-7 mb-3'>
                            <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                            Checking which contacts already exist...
                        </div>
                    )}
                    {preview && (
                        <div className='d-flex flex-wrap gap-6 bg-light-primary rounded p-4 mb-4'>
                            <div>
                                <div className='fs-2 fw-bolder text-success'>{preview.created}</div>
                                <div className='fs-8 text-gray-600 text-uppercase'>
                                    will be created
                                </div>
                            </div>
                            <div>
                                <div className='fs-2 fw-bolder text-primary'>
                                    {preview.updated - preview.unchanged}
                                </div>
                                <div className='fs-8 text-gray-600 text-uppercase'>
                                    will update an existing contact
                                </div>
                            </div>
                            {preview.unchanged > 0 && (
                                <div>
                                    <div className='fs-2 fw-bolder text-gray-500'>
                                        {preview.unchanged}
                                    </div>
                                    <div className='fs-8 text-gray-600 text-uppercase'>
                                        already up to date
                                    </div>
                                </div>
                            )}
                            {preview.skipped > 0 && (
                                <div>
                                    <div className='fs-2 fw-bolder text-danger'>
                                        {preview.skipped}
                                    </div>
                                    <div className='fs-8 text-gray-600 text-uppercase'>
                                        skipped &middot; new, no first name
                                    </div>
                                </div>
                            )}
                            <div className='separator separator-dashed d-none d-md-block'></div>
                            <div className='d-flex align-items-center text-gray-600 fs-7 flex-grow-1'>
                                Matched on email address. Cells shaded{' '}
                                <span className='badge badge-light-warning mx-1'>amber</span> will
                                change; cells marked{' '}
                                <span className='badge badge-danger mx-1'>Removed</span> say DELETE
                                and clear the stored value. Blank cells keep what is stored, and a
                                contact's photo and theme are never touched.
                            </div>
                        </div>
                    )}
                    {/* end::What this import will do */}

                    {/* begin::Preview */}
                    {tableData.data.length > 0 && (
                        <>
                            <div
                                className='table-responsive border rounded'
                                style={{ maxHeight: '45vh', overflowY: 'auto' }}
                            >
                                <table
                                    id='kt_table_contacts_import'
                                    className='table table-row-bordered table-row-dashed align-middle gy-3 gs-4 mb-0'
                                >
                                    <thead
                                        className='bg-light'
                                        style={{ position: 'sticky', top: 0, zIndex: 1 }}
                                    >
                                        <tr className='text-start text-gray-600 fw-bolder fs-8 text-uppercase'>
                                            <th className='w-50px'>#</th>
                                            {preview && <th className='w-125px'>Action</th>}
                                            {tableData.header.map((column) => (
                                                <th key={column} className='text-nowrap'>
                                                    {column}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className='text-gray-700 fs-7'>
                                        {previewRows.map((contact, rowIndex) => {
                                            const changed = changedColumnsFor(
                                                preview?.rows[rowIndex]
                                            )
                                            return (
                                            <tr key={rowIndex}>
                                                <td className='text-gray-400'>{rowIndex + 1}</td>
                                                {preview && (
                                                    <td>{renderAction(preview, rowIndex)}</td>
                                                )}
                                                {tableData.header.map((column) => {
                                                    let key = column as keyof ContactImport
                                                    let cellData = contact[key] ? contact[key] : ''
                                                    let data = cellData as string
                                                    if (key === 'address') {
                                                        data = (cellData as Address[])[1]
                                                            .street as string
                                                    }
                                                    // Grouped fields merge entry by entry, so a
                                                    // blank cell beside a changed one changes
                                                    // nothing — only filled or DELETE cells do.
                                                    const saysDelete =
                                                        typeof data === 'string' &&
                                                        data.trim().toLowerCase() === 'delete'
                                                    const willChange =
                                                        changed.has(column) &&
                                                        !!data &&
                                                        `${data}`.trim() !== ''
                                                    return (
                                                        <td
                                                            key={column}
                                                            className={clsx(
                                                                'text-nowrap',
                                                                willChange &&
                                                                    (saysDelete
                                                                        ? 'bg-light-danger'
                                                                        : 'bg-light-warning fw-bold')
                                                            )}
                                                            title={
                                                                saysDelete
                                                                    ? willChange
                                                                        ? 'DELETE — the stored value will be removed.'
                                                                        : 'DELETE — nothing is stored here to remove.'
                                                                    : willChange
                                                                    ? `This will overwrite the stored value. New value: ${data}`
                                                                    : data
                                                            }
                                                        >
                                                            {saysDelete ? (
                                                                <span
                                                                    className={clsx(
                                                                        'badge',
                                                                        willChange
                                                                            ? 'badge-danger'
                                                                            : 'badge-light'
                                                                    )}
                                                                >
                                                                    {willChange ? 'Removed' : 'DELETE'}
                                                                </span>
                                                            ) : data ? (
                                                                data
                                                            ) : (
                                                                <span className='text-gray-400'>
                                                                    &mdash;
                                                                </span>
                                                            )}
                                                        </td>
                                                    )
                                                })}
                                            </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {tableData.data.length > PREVIEW_ROW_LIMIT && (
                                <div className='fs-7 text-gray-500 mt-2'>
                                    Showing the first {PREVIEW_ROW_LIMIT} of{' '}
                                    {tableData.data.length} rows. All of them will be imported.
                                </div>
                            )}
                        </>
                    )}
                    {/* end::Preview */}

                    {importResult && (
                        <div className='alert alert-success d-flex align-items-center mt-5 mb-0'>
                            <KTSVG
                                path='/media/icons/duotune/general/gen043.svg'
                                className='svg-icon-2 svg-icon-success me-3'
                            />
                            <span>
                                {[
                                    `${importResult.created} contact(s) created`,
                                    importResult.updated > 0 &&
                                        `${importResult.updated} updated because the email address already existed`,
                                    importResult.skipped > 0 &&
                                        `${importResult.skipped} skipped — new contact with no first name`,
                                ]
                                    .filter(Boolean)
                                    .join(', ') + '.'}
                            </span>
                        </div>
                    )}

                    {/* begin::Actions */}
                    <div className='d-flex justify-content-end gap-2 pt-6'>
                        <button
                            type='button'
                            className='btn btn-light'
                            disabled={isSubmitting}
                            onClick={() => {
                                reset()
                                // Once the import has run there is nothing left
                                // to discard, so the same button dismisses the
                                // modal instead.
                                if (importResult) {
                                    setState(false)
                                }
                            }}
                        >
                            {importResult ? 'Close' : 'Discard'}
                        </button>
                        <button
                            type='button'
                            className='btn btn-primary'
                            disabled={
                                isSubmitting ||
                                importableCount === 0 ||
                                importResult !== undefined
                            }
                            onClick={async () => {
                                if (!tableData.data.length) {
                                    return
                                }
                                setIsSubmitting(true)
                                setFileError('')
                                try {
                                    const result = await importContact(tableData.data)
                                    setImportResult(result)
                                    // Rows that matched an existing email were
                                    // updated in place, so the list behind the
                                    // modal is now stale either way.
                                    refetch()
                                } catch (e: any) {
                                    // Without this the button simply returned to
                                    // normal and the user was left guessing
                                    // whether anything had been imported.
                                    const status = e?.response?.status
                                    setFileError(
                                        status === 413
                                            ? 'This file is too large for the server to accept in one request. Split it into smaller files and import them one at a time.'
                                            : `The import failed and nothing was saved${
                                                  status ? ` (error ${status})` : ''
                                              }. Please try again, or contact support if it keeps happening.`
                                    )
                                } finally {
                                    setIsSubmitting(false)
                                }
                            }}
                        >
                            {isSubmitting ? (
                                <span className='d-flex align-items-center'>
                                    Importing...
                                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                </span>
                            ) : (
                                `Import ${importableCount} contact${
                                    importableCount === 1 ? '' : 's'
                                }`
                            )}
                        </button>
                    </div>
                    {/* end::Actions */}
                </>
            )}
        </div>
    )
}

// Renders what the server said row `rowIndex` (0-based) will do.
function renderAction(preview: ImportPreview, rowIndex: number) {
    const row = preview.rows[rowIndex]
    if (!row) {
        return null
    }
    if (row.action === 'skip') {
        return (
            <span
                className='badge badge-light-danger'
                title='A new contact needs a first name. Add one to this row — or, if you meant to update an existing contact, check the email address matches it.'
            >
                Skipped
            </span>
        )
    }
    if (row.action === 'create') {
        return (
            <span
                className='badge badge-light-success'
                title={
                    row.noEmail
                        ? 'This row has no email address, so it can never match an existing contact.'
                        : 'No existing contact uses this email address.'
                }
            >
                New contact
            </span>
        )
    }
    if (row.duplicateOfRow) {
        return (
            <span
                className='badge badge-light-primary'
                title={`Row ${row.duplicateOfRow} in this file already creates ${row.matchedEmail}, so this row updates it instead of adding a duplicate.`}
            >
                Updates row {row.duplicateOfRow}
            </span>
        )
    }

    const who = `${row.matchedContact?.name || 'an existing contact'}${
        row.matchedContact?.organisation ? ` (${row.matchedContact.organisation})` : ''
    }`
    const count = row.changedFields?.length || 0

    if (count === 0) {
        return (
            <span
                className='badge badge-light'
                title={`Matches ${who} on ${row.matchedEmail}, and every value in this row is already stored. Importing changes nothing.`}
            >
                No change
            </span>
        )
    }

    return (
        <span
            className='badge badge-light-primary'
            title={`Matches ${who} on ${row.matchedEmail}. Will overwrite: ${row.changedFields?.join(
                ', '
            )}.`}
        >
            {count} field{count === 1 ? '' : 's'} change
        </span>
    )
}

export { DropZone }
